import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import { apiFetch } from '../utils/api';
import Snackbar from './Snackbar';

function PropertyEdit() {
  const { id } = useParams();
  const { state, dispatch } = useGlobalState();
  const { properties } = state;

  const [formData, setFormData] = useState({
    propertyId: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    parcelNumber: '',
    bedrooms: '',
    bathrooms: '',
    acreage: '',
    livingSqFt: '',
    totalSqFt: '',
    yearBuilt: '',
    zoning: '',
    imageUrl: '',
    propertyType: '',
    propertySubtype: '',
    propertyDescription: '',
    listPrice: '',
    mls_id: '',
    listingLink: '',
    buyerFee: '',
    expirationDate: '',
    listingStatus: '',
    creatorID: '',
    listingUserId: ''
  });
  const [snackbar, setSnackbar] = useState({ show: false, message: '', type: 'success' });
  const [isListing, setIsListing] = useState(false);

  const navigate = useNavigate();

  const mapPropertyToForm = (property) => ({
    propertyId: property.propertyId ?? property.id ?? id,
    streetAddress: property.streetAddress ?? property.street_address ?? '',
    city: property.city ?? '',
    state: property.state ?? '',
    zipCode: property.zipCode ?? property.zip_code ?? '',
    parcelNumber: property.parcelNumber ?? property.parcel_number ?? '',
    bedrooms: property.bedrooms ?? '',
    bathrooms: property.bathrooms ?? '',
    acreage: property.acreage ?? '',
    livingSqFt: property.livingSqFt ?? property.living_sqft ?? '',
    totalSqFt: property.totalSqFt ?? property.total_sqft ?? '',
    yearBuilt: property.yearBuilt ?? property.year_built ?? '',
    zoning: property.zoning ?? '',
    imageUrl: property.imageUrl ?? property.image_url ?? '',
    propertyType: property.propertyType ?? property.property_type ?? '',
    propertySubtype: property.propertySubtype ?? property.property_subtype ?? '',
    propertyDescription: property.propertyDescription ?? property.property_description ?? '',
    listPrice: property.listPrice ?? '',
    mls_id: property.mls_id ?? property.mlsId ?? '',
    listingLink: property.listingLink ?? property.listing_link ?? '',
    buyerFee: property.buyerFee ?? '',
    expirationDate: property.expirationDate ?? property.expiration_date ?? '',
    listingStatus: property.listingStatus ?? property.status ?? '',
    creatorID: property.creatorID ?? property.creator_id ?? '',
    listingUserId: property.listingUserId ?? property.listing_user_id ?? '',
  });

  // Resolve property and ownership
  useEffect(() => {
    const property = properties.find((p) => String(p.propertyId) === String(id));
    if (!property) return; // wait until properties load

    const currentUserId = state?.user?.userId ?? state?.user?.id;
    const listingOwnerMatch =
      currentUserId != null &&
      property?.listingUserId != null &&
      String(currentUserId) === String(property.listingUserId);
    const listingFlag = property?.listingUserId != null;
    const canEditProperty = !listingFlag || listingOwnerMatch;

    if (!canEditProperty) {
      // Block access and route back to read-only detail
      setTimeout(() => {
        navigate(`/property/${id}`, { replace: true });
      }, 0);
      // Surface feedback
      setSnackbar({ show: true, message: '❌ You do not have permission to edit this listing', type: 'danger' });
      return;
    }

    // Preload form once authorized
    setIsListing(listingFlag);
    setFormData((prev) => ({
      ...prev,
      ...mapPropertyToForm(property),
    }));
  }, [id, properties, navigate, state?.user]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Double-check authorization server-side and client-side
    const currentUserId = state?.user?.userId ?? state?.user?.id;
    const listingOwnerMatch =
      currentUserId != null &&
      formData?.listingUserId != null &&
      String(currentUserId) === String(formData.listingUserId);

    if (isListing && !listingOwnerMatch) {
      setSnackbar({ show: true, message: '❌ You do not have permission to edit this listing', type: 'danger' });
      return;
    }

    const payload = {
      id: formData.propertyId || id,
      street_address: formData.streetAddress || null,
      city: formData.city || null,
      state: formData.state || null,
      zip_code: formData.zipCode || null,
      parcel_number: formData.parcelNumber || null,
      acreage: formData.acreage || null,
      bedrooms: formData.bedrooms || null,
      bathrooms: formData.bathrooms || null,
      year_built: formData.yearBuilt || null,
      living_sqft: formData.livingSqFt || null,
      total_sqft: formData.totalSqFt || null,
      property_type: formData.propertyType || null,
      property_subtype: formData.propertySubtype || null,
      zoning: formData.zoning || null,
      property_description: formData.propertyDescription || null,
    };

    if (isListing) {
      payload.expiration_date = formData.expirationDate || null;
      payload.status = formData.listingStatus || 'Pending';
    }

    try {
      const result = await apiFetch('editProperty', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (!result.success) {
        setSnackbar({ show: true, message: `❌ ${result.message}`, type: 'danger' });
        return;
      }

      dispatch({
        type: 'UPDATE_PROPERTY',
        payload: {
          ...formData,
          propertyId: formData.propertyId,
        },
      });
      setSnackbar({ show: true, message: '✅ Property saved', type: 'success' });
      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      console.error('Property update failed:', err);
      setSnackbar({ show: true, message: `❌ Failed to update: ${err.message}`, type: 'danger' });
    }
  };

  return (
    <div className="container">
      <Snackbar
        show={snackbar.show}
        message={snackbar.message}
        type={snackbar.type}
        onClose={() => setSnackbar((s) => ({ ...s, show: false }))}
      />
      <div className="d-flex justify-content-between align-items-center">
        <button className="btn btn-light btn-sm" onClick={() => navigate(-1)}>
          &larr; Back
        </button>
      </div>
      <h1>Edit Property</h1>
      <form onSubmit={handleSubmit} className="container mt-2">
        <div className="row mb-2">
          {[
            { name: 'streetAddress', label: 'Street Address' },
            { name: 'city', label: 'City' },
            { name: 'state', label: 'State' },
            { name: 'zipCode', label: 'Zip Code' },
            { name: 'parcelNumber', label: 'Parcel Number' },
            { name: 'bedrooms', label: 'Bedrooms', type: 'number' },
            { name: 'bathrooms', label: 'Bathrooms', type: 'number' },
            { name: 'acreage', label: 'Acreage', type: 'number', step: '0.01' },
            { name: 'livingSqFt', label: 'Living SqFt', type: 'number' },
            { name: 'totalSqFt', label: 'Total SqFt', type: 'number' },
            { name: 'yearBuilt', label: 'Year Built', type: 'number' },
            { name: 'zoning', label: 'Zoning' },
            { name: 'propertyType', label: 'Property Type' },
            { name: 'propertySubtype', label: 'Property Subtype' },
            { name: 'imageUrl', label: 'Image URL' },
          ].map(({ name, label, type = 'text', step }, idx) => (
            <div className="col-md-3 mb-2" key={idx}>
              <label htmlFor={name} className="form-label">
                {label}
              </label>
              <input
                type={type}
                className="form-control"
                id={name}
                name={name}
                value={formData[name] || ''}
                onChange={handleChange}
                step={step}
              />
            </div>
          ))}

          <div className="col-12 mb-3">
            <label htmlFor="propertyDescription" className="form-label">
              Property Description
            </label>
            <textarea
              className="form-control"
              id="propertyDescription"
              name="propertyDescription"
              rows={3}
              value={formData.propertyDescription || ''}
              onChange={handleChange}
            />
          </div>

          {isListing && (
            <>
              <div className="col-md-3 mb-2">
                <label htmlFor="listPrice" className="form-label">
                  List Price
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="listPrice"
                  name="listPrice"
                  value={formData.listPrice || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="mls_id" className="form-label">
                  MLS ID
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="mls_id"
                  name="mls_id"
                  value={formData.mls_id || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="listingLink" className="form-label">
                  Listing Link
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="listingLink"
                  name="listingLink"
                  value={formData.listingLink || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="buyerFee" className="form-label">
                  Buyer Fee (%)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="buyerFee"
                  name="buyerFee"
                  value={formData.buyerFee || ''}
                  onChange={handleChange}
                  step="0.01"
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="expirationDate" className="form-label">
                  Expiration Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="expirationDate"
                  name="expirationDate"
                  value={formData.expirationDate || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-2">
                <label htmlFor="listingStatus" className="form-label">
                  Listing Status
                </label>
                <select
                  className="form-select"
                  id="listingStatus"
                  name="listingStatus"
                  value={formData.listingStatus || 'Pending'}
                  onChange={handleChange}
                >
                  {["Pending", "Active", "Under Contract", "Sold", "Withdrawn"].map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {statusOption}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className="row mb-2 text-center">
          <div className="col-md-12">
            <button type="submit" className="btn btn-primary w-50">
              Update Property
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default PropertyEdit;
